import React, { Fragment, useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useQuery } from "@tanstack/react-query";

import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import HeaderButton from "../../components/ui-components/HeaderButton.js";
import RightSheet from "../../components/ui-components/RightSheet";
import FormButton from "../../components/form-components/FormButton";
import { Dropdown } from "../../components/form-components/Dropdown";
import { MoneyInput } from "../../components/form-components/MoneyInput";
import Modal from "../../components/ui-components/Modal";
import Skeleton from "../../components/ui-components/Skeleton";
import {
  useDelegationsQuery,
  useCreateDelegationMutation,
  useDisableDelegationMutation,
} from "./hooks/useApprovalsQueries";
import { listUsers } from "../users/api";
import { toast } from "../../components/ui-components/Toast";
import "../../components/ui-components/DataTable.css";

const SCOPE_OPTIONS = [
  { value: "all", label: "All object types" },
  { value: "budget_period_version", label: "Budget period version" },
];

const DelegationsPage = () => {
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [disableModal, setDisableModal] = useState({ open: false, delegation: null });

  const [form, setForm] = useState({
    delegate_user_id: "",
    object_type: "all",
    starts_at: "",
    ends_at: "",
    max_amount: null,
  });
  const [selectedUser, setSelectedUser] = useState(null);

  const { data: delegations, isLoading, error, refetch } = useDelegationsQuery();
  const createMutation = useCreateDelegationMutation();
  const disableMutation = useDisableDelegationMutation();

  const usersQuery = useQuery(["users"], () => listUsers({ limit: 200 }));
  const userOptions = useMemo(() => {
    const raw = usersQuery.data?.users?.items || [];
    return raw.map((u) => ({
      value: u.id ?? u.ID,
      label: [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || String(u.id ?? u.ID ?? ""),
      subtitle: u.function || undefined,
    }));
  }, [usersQuery.data]);

  const handleCreateSubmit = async () => {
    if (!form.delegate_user_id) {
      toast.error("A delegate is required");
      return;
    }
    try {
      const payload = {
        delegate_user_id: form.delegate_user_id,
        scope_json: { object_type: form.object_type || "all" },
      };
      if (form.starts_at) payload.starts_at = new Date(form.starts_at).toISOString();
      if (form.ends_at) payload.ends_at = new Date(form.ends_at).toISOString();
      if (form.max_amount != null) {
        payload.max_amount = form.max_amount;
      }
      await createMutation.mutateAsync(payload);
      toast.success("Delegation created");
      setCreateDrawerOpen(false);
      setForm({ delegate_user_id: "", object_type: "all", starts_at: "", ends_at: "", max_amount: null });
      setSelectedUser(null);
    } catch (e) {
      // Error already handled in mutation
    }
  };

  const handleDisable = async () => {
    if (!disableModal.delegation) return;
    try {
      await disableMutation.mutateAsync(disableModal.delegation.id);
      toast.success("Delegation disabled");
      setDisableModal({ open: false, delegation: null });
    } catch (e) {
      // Error already handled in mutation
    }
  };

  const scopeSummary = (d) => {
    const scope = d.scope_json;
    if (!scope) return "all";
    return scope.object_type || "all";
  };

  const formatDateRange = (startsAt, endsAt) => {
    if (!startsAt && !endsAt) return "—";
    const s = startsAt ? new Date(startsAt).toLocaleDateString() : "—";
    const e = endsAt ? new Date(endsAt).toLocaleDateString() : "—";
    return `${s} – ${e}`;
  };

  if (isLoading) {
    return (
      <Fragment>
        <AdministrationHeader title="Settings" showPeriodSelector={false} />
        <div className="text-left flex flex-auto flex-col ml-10 mt-6">
          <Skeleton width={400} height={200} />
        </div>
      </Fragment>
    );
  }

  if (error) {
    const isUnauthorized = error?.status === 401 || error?.status === 403;
    return (
      <Fragment>
        <AdministrationHeader title="Settings" showPeriodSelector={false} />
        <div className="text-left flex flex-auto flex-col ml-10 mt-6">
          <div className="flex flex-col items-center justify-center h-64">
            <div className="text-danger mb-2">
              {isUnauthorized ? "Not authorized" : "Error loading delegations"}
            </div>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-primary text-white rounded-ctrl hover:bg-primary-hover"
            >
              Retry
            </button>
          </div>
        </div>
      </Fragment>
    );
  }

  const footer = (
    <div className="flex gap-2 justify-end">
      <FormButton title="Cancel" style="tertiary" onClick={() => setCreateDrawerOpen(false)} />
      <FormButton
        title={createMutation.isPending ? "Creating..." : "Create"}
        style="primary"
        onClick={handleCreateSubmit}
        disabled={!form.delegate_user_id || createMutation.isPending}
      />
    </div>
  );

  return (
    <Fragment>
      <AdministrationHeader title="Control Policies" showPeriodSelector={false} />
      <div className="text-left flex flex-auto flex-col ml-10 mt-6 border-solid border-border">
        <div className="flex flex-row justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-text">Delegations</h2>
          <HeaderButton
            title="New delegation"
            type="primary"
            icon={icon({ name: "plus", style: "regular" })}
            onClick={() => setCreateDrawerOpen(true)}
          />
        </div>

        {(!delegations || delegations.length === 0) ? (
          <div className="flex flex-col items-center justify-center h-60 mr-24">
            <div className="mb-3">
              <FontAwesomeIcon
                icon={icon({ name: "user-group", family: "sharp", style: "light" })}
                className="text-text-3 text-2xl"
              />
            </div>
            <p className="text-lg font-medium text-text mb-1">
              No delegations yet
            </p>
            <p className="mb-5 text-sm text-text-2 text-center max-w-sm">
              Delegate your approval authority to a trusted colleague for when you are unavailable.
            </p>
            <FormButton
              title="Create delegation"
              style="secondary"
              onClick={() => setCreateDrawerOpen(true)}
            />
          </div>
        ) : (
          <div className="bg-card border border-card-bd rounded-card overflow-hidden p-2">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Delegator</th>
                  <th>Delegate</th>
                  <th>Date range</th>
                  <th>Scope</th>
                  <th>Max amount</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {delegations.map((d) => (
                  <tr key={d.id}>
                    <td className="text-text">{d.delegator_user_id || "—"}</td>
                    <td className="text-text">{d.delegate_user_id || "—"}</td>
                    <td>{formatDateRange(d.starts_at, d.ends_at)}</td>
                    <td>{scopeSummary(d)}</td>
                    <td>{d.max_amount != null ? d.max_amount : "—"}</td>
                    <td>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${d.is_enabled ? "bg-success-soft text-success" : "bg-surface-2 text-text-2"}`}>
                        {d.is_enabled ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td className="text-right">
                      {d.is_enabled && (
                        <button
                          onClick={() => setDisableModal({ open: true, delegation: d })}
                          className="text-danger hover:opacity-80 text-sm"
                        >
                          Disable
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <RightSheet
        open={createDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
        title="Create delegation"
        subtitle="Delegate your approval authority to another user."
        footer={footer}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-2 mb-2">Delegate (required)</label>
            <Dropdown
              options={userOptions}
              selectedValue={selectedUser}
              setSelectedValue={(opt) => {
                setSelectedUser(opt);
                setForm((f) => ({ ...f, delegate_user_id: opt ? String(opt.value) : "" }));
              }}
              dropdownWidth={300}
              twoLine
              menuPortalTarget={document.body}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-2 mb-2">Scope</label>
            <Dropdown
              options={SCOPE_OPTIONS}
              selectedValue={SCOPE_OPTIONS.find((o) => o.value === form.object_type) || SCOPE_OPTIONS[0]}
              setSelectedValue={(opt) => setForm((f) => ({ ...f, object_type: opt.value }))}
              dropdownWidth={300}
              menuPortalTarget={document.body}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-2 mb-2">Starts at (optional)</label>
            <input
              type="datetime-local"
              className="w-full p-2 border border-border rounded-ctrl text-sm"
              value={form.starts_at}
              onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-2 mb-2">Ends at (optional)</label>
            <input
              type="datetime-local"
              className="w-full p-2 border border-border rounded-ctrl text-sm"
              value={form.ends_at}
              onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-2 mb-2">Max amount (optional)</label>
            <MoneyInput
              value={form.max_amount}
              onChange={(n) => setForm((f) => ({ ...f, max_amount: n }))}
              allowNegative={false}
              min={0}
              decimals={2}
              placeholder="0,00"
              maxWidth="300px"
            />
          </div>
        </div>
      </RightSheet>

      <Modal
        show={disableModal.open}
        close={() => setDisableModal({ open: false, delegation: null })}
        title="Disable delegation?"
        description="This delegation will no longer allow the delegate to act on your behalf."
        confirmButton="Disable"
        cancelButton="Cancel"
        alertIcon
        confirmAction={handleDisable}
        icon={icon({ name: "circle-xmark", style: "solid" })}
      />
    </Fragment>
  );
};

export default DelegationsPage;
